import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { adminStatsService, realtimeService } from '../../../services/supabaseService';

const RecentActivity = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [recentOrders, recentTransactions] = await Promise.all([
          adminStatsService?.getRecentOrders(8),
          adminStatsService?.getRecentTransactions(5),
        ]);
        setOrders(recentOrders);
        setTransactions(recentTransactions);
      } catch (err) {
        console.error('Failed to load activity:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Real-time new orders
    const channel = realtimeService?.subscribeToNewOrders((payload) => {
      setOrders(prev => [payload?.new, ...prev]?.slice(0, 8));
    });

    return () => realtimeService?.unsubscribe(channel);
  }, []);

  const tabs = [
    { id: 'all', label: 'All Activity', count: orders?.length },
    { id: 'transactions', label: 'Transactions', count: transactions?.length },
  ];

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return new Date(timestamp)?.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': case 'completed': return 'success';
      case 'pending': case 'confirmed': return 'warning';
      case 'cancelled': case 'failed': return 'error';
      case 'en_route': case 'picked_up': return 'secondary';
      default: return 'text-muted';
    }
  };

  const getOrderIcon = (serviceType) => {
    switch (serviceType) {
      case 'delivery': return 'Package';
      case 'errand': return 'ShoppingBag';
      default: return 'Car';
    }
  };

  const renderOrdersView = () => (
    <div className="space-y-3">
      {loading ? (
        <div className="text-center py-6 text-text-muted text-sm">Loading activity...</div>
      ) : orders?.length === 0 ? (
        <div className="text-center py-6 text-text-muted text-sm">No recent activity</div>
      ) : orders?.map((order) => (
        <div key={order?.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-surface-hover transition-colors duration-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <Icon name={getOrderIcon(order?.serviceType)} size={16} color="var(--color-primary)" />
            </div>
            <div>
              <p className="font-medium text-text-primary">{order?.orderNumber}</p>
              <p className="text-sm text-text-muted">
                {order?.customer?.fullName || 'Customer'} · {order?.serviceType}
              </p>
            </div>
          </div>
          <div className="text-right">
            <span className={`text-xs px-2 py-1 rounded-full bg-${getStatusColor(order?.status)}-100 text-${getStatusColor(order?.status)}`}>
              {order?.status?.replace('_', ' ')}
            </span>
            <p className="text-xs text-text-muted mt-1">{formatTime(order?.createdAt)}</p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTransactionsView = () => (
    <div className="space-y-3">
      {loading ? (
        <div className="text-center py-6 text-text-muted text-sm">Loading transactions...</div>
      ) : transactions?.length === 0 ? (
        <div className="text-center py-6 text-text-muted text-sm">No recent transactions</div>
      ) : transactions?.map((txn) => (
        <div key={txn?.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-surface-hover transition-colors duration-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center">
              <Icon name="CreditCard" size={16} color="var(--color-success)" />
            </div>
            <div>
              <p className="font-medium text-text-primary">{txn?.userName || 'User'}</p>
              <p className="text-sm text-text-muted">{txn?.type?.replace('_', ' ')} · {txn?.paymentMethod}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-semibold text-text-primary">KES {txn?.amount?.toLocaleString()}</p>
            <span className={`text-xs px-2 py-1 rounded-full bg-${getStatusColor(txn?.status)}-100 text-${getStatusColor(txn?.status)}`}>
              {txn?.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary">Recent Activity</h3>
        <button className="text-sm text-primary hover:text-primary-dark transition-colors duration-200">
          View All
        </button>
      </div>
      <div className="flex space-x-1 mb-4 border-b border-border">
        {tabs?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors duration-200 border-b-2 -mb-px ${
              activeTab === tab?.id
                ? 'border-primary text-primary' :'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            {tab?.label}
            <span className="ml-1.5 text-xs bg-surface px-1.5 py-0.5 rounded-full">{tab?.count}</span>
          </button>
        ))}
      </div>
      <div className="max-h-96 overflow-y-auto">
        {activeTab === 'all' ? renderOrdersView() : renderTransactionsView()}
      </div>
    </div>
  );
};

export default RecentActivity;